import Lottie from "lottie-react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Hourglass02 } from "@untitled-ui/icons-react";
import Button from "@/components/button/button";
import { IShopItem, ShopItemStatus, ShopItemStatusEnum } from "@/modules/shopModule/types";
import confetti from "@/assets/lottie/modal_confetti.json";
import successAnimation from "../../../assets/json/success.json";

import styles from "../ShopItemModal.module.scss";

interface ShopItemSuccessProps {
  handleClosePopup: () => void;
  item: IShopItem;
  status: ShopItemStatus;
}

const ShopItemSuccess = ({ handleClosePopup, item, status }: ShopItemSuccessProps) => {
  const { t, i18n } = useTranslation();
  const itemName = item.nameTranslations[i18n.language] || item.name;
  const categoryName = item.category.translations[i18n.language] || item.category.name;
  const isPending = status === ShopItemStatusEnum.PENDING;
  const isError = status === ShopItemStatusEnum.ERROR;

  const renderStatusVisual = () => {
    if (isPending) {
      return <Hourglass02 className={`${styles.statusIcon} ${styles.statusIconWarning}`} />;
    }

    if (isError) {
      return <AlertTriangle className={`${styles.statusIcon} ${styles.statusIconError}`} />;
    }

    return (
      <>
        <div className={styles.successConfetti} aria-hidden="true">
          <Lottie animationData={confetti} loop={true} />
        </div>
        <div className={styles.successCheckCircle} aria-hidden="true">
          <Lottie animationData={successAnimation} loop={false} />
        </div>
      </>
    );
  };

  const statusTitle = isPending
    ? t("shop.purchaseWarningTitle", "Warning / Progress")
    : isError
      ? t("shop.purchaseError", "Error")
      : t("shop.successfullyBought");

  return (
    <div className={styles.successContainer}>
      <div className={styles.successVisual} aria-label={statusTitle}>
        {renderStatusVisual()}
      </div>

      <h2
        className={`${styles.successTitle} ${
          isPending ? styles.warningStatusTitle : isError ? styles.errorStatusTitle : ""
        }`}
      >
        {statusTitle}
      </h2>

      <div className={styles.itemContainer}>
        <img src={item.image} alt={itemName} className={styles.itemImg} />
        <div className={styles.itemDetails}>
          <p className={styles.itemName}>{itemName}</p>
          <p className={styles.itemCategory}>{categoryName}</p>
        </div>
      </div>

      <div className="flex justify-content-center align-items-center">
        <Button
          onClick={handleClosePopup}
          className={styles.backBtn}
          type="primary"
          label={t("shop.backToShop")}
        />
      </div>
    </div>
  );
};

export default ShopItemSuccess;
